import React from 'react';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import {
  Box,
  Button,
  Drawer,
  Popover,
  Typography,
  Badge,
  IconButton,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Chip from '@mui/material/Chip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import ListItemIcon from '@mui/material/ListItemIcon';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';

/**
 * 알림을 멈출 수 있는 종류인지.
 *
 * 사건 알림(결재 요청됨 등)은 읽으면 끝이라 멈출 대상이 아니다.
 * 상태 리마인드(미출고가 아직 남아있다)만 조건이 유지되는 동안 반복되므로 여기에 해당한다.
 */
const canSilence = (noti) =>
  noti?.targetType === 'SHIPMENT_CANDIDATE' && Boolean(noti?.targetId);

/**
 * 같은 대상의 반복 알림을 한 줄로 묶는다.
 *
 * 미출고 리마인드는 조건이 유지되는 동안 계속 쌓여서, 같은 건이 목록에 수십 줄로 늘어선다.
 * 줄마다 끄기 버튼을 다는 건 의미가 없다 — 대상이 같으니 한 번만 끄면 된다.
 * 묶어두면 목록도 짧아지고 "몇 번 왔는지"가 오히려 잘 보인다.
 */
const groupNoties = (noties) => {
  const groups = [];
  const indexByKey = new Map();

  noties.forEach((noti) => {
    const key = canSilence(noti) ? `${noti.targetType}:${noti.targetId}` : null;
    if (key && indexByKey.has(key)) {
      const group = groups[indexByKey.get(key)];
      group.count += 1;
      group.notiIds.push(noti.notiId);
      return;
    }
    const group = { noti, count: 1, notiIds: [noti.notiId] };
    if (key) indexByKey.set(key, groups.length);
    groups.push(group);
  });

  return groups;
};

const NotiBox = ({
  noties,
  notiCount,
  onConfirmNoties,
  onConfirmNotiAll,
  onRefresh,
  onSnooze,
  onIgnore,
  onIgnoreAll,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const safeNoties = noties || [];
  const groups = groupNoties(safeNoties);
  // 끌 수 있는 것만 모은다. 결재 요청 같은 사건 알림은 대상이 아니다.
  const silenceableGroups = groups.filter((g) => canSilence(g.noti));

  // 끄기 메뉴. 줄마다 버튼을 달면 목록이 버튼으로 뒤덮인다.
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuNoti, setMenuNoti] = React.useState(null);

  const openMenu = (event, noti) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuNoti(noti);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuNoti(null);
  };

  const renderContent = (popupState) => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100vw', sm: 420, md: 600 },
          maxWidth: { xs: '100vw', sm: 420, md: 600 },
          maxHeight: { xs: '75dvh', md: 400 },
          overflowY: 'auto',
        }}
      >
        <List sx={{ width: '100%', minWidth: 0 }}>
          {safeNoties.length === 0 && (
            <ListItem alignItems="center">
              확인할 알림이 없습니다
            </ListItem>
          )}

          {groups.length > 0 &&
            groups.map(({ noti, count }, index) => {
              return (
                <React.Fragment key={index}>
                  <ListItem dense divider disablePadding>
                    <ListItemButton
                      onClick={() => {
                        popupState.close();
                        onConfirmNoties(noti);
                      }}
                    >
                      <ListItemText
                        secondary={
                          <React.Fragment>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              {noti.viewYn === 'N' && (
                                <Badge
                                  badgeContent={noti.viewYn}
                                  size="small"
                                  color="error"
                                />
                              )}
                              <Chip
                                label={
                                  noti.notiTyp === 'TECH'
                                    ? `기술`
                                    : `영업`
                                }
                                size="small"
                                color={
                                  noti.notiTyp === 'TECH'
                                    ? `info`
                                    : `warning`
                                }
                              />

                              <Typography
                                component="div"
                                variant="body2"
                                align="left"
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {noti.notiTxt}
                              </Typography>

                              {/* 같은 건이 몇 번 왔는지. 묶은 줄에만 보인다. */}
                              {count > 1 && (
                                <Chip
                                  label={`${count}회`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                              {canSilence(noti) && (
                                <IconButton
                                  size="small"
                                  aria-label="알림 끄기"
                                  onClick={(e) => openMenu(e, noti)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              {/* 반복되는 리마인드는 읽어도 조건이 그대로면 또 온다.
                                  사용자가 알림을 멈출 수단이 여기 없으면 "확인했는데 왜 또 와?" 가 된다. */}
                              <Box />
                              <Typography
                                component="div"
                                variant="body2"
                                color="text.disabled"
                                align="left"
                              >
                                {noti.crtDt}
                              </Typography>
                            </Box>
                          </React.Fragment>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              );
            })}
        </List>

        {/* 끄기 메뉴. 줄마다 버튼을 다는 대신 여기로 모았다. */}
        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
          <MenuItem
            onClick={() => { const target = menuNoti; closeMenu(); onSnooze(target); }}
          >
            <ListItemIcon><NotificationsPausedIcon fontSize="small" /></ListItemIcon>
            3일 뒤에 다시 알림
          </MenuItem>
          <MenuItem
            onClick={() => { const target = menuNoti; closeMenu(); onIgnore(target); }}
          >
            <ListItemIcon><NotificationsOffIcon fontSize="small" /></ListItemIcon>
            이 건은 그만 받기
          </MenuItem>
        </Menu>
      </Box>
      <Divider />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 1.5, py: 1 }}
      >
        {/* 새로고침은 자주 쓰지만 결과를 바꾸지 않는다. 아이콘만 두고 자리를 아낀다. */}
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={onRefresh} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* 반복 알림을 한 번에 끄기. 여러 건이 같은 이유로 도는 경우가 대부분이다. */}
        {silenceableGroups.length > 0 && (
          <Button
            size="small"
            color="inherit"
            startIcon={<NotificationsOffIcon fontSize="small" />}
            onClick={() => onIgnoreAll(silenceableGroups.map((g) => g.noti))}
          >
            전체 그만 받기
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/*
          예전 이름은 "전체 삭제" 였는데 실제로는 지우지 않고 모두 읽음 처리만 한다.
          지운다고 적혀 있으면 누르기가 무섭고, 실제 동작과도 다르다.
        */}
        <Button
          size="small"
          color="inherit"
          startIcon={<DoneAllIcon fontSize="small" />}
          disabled={safeNoties.length === 0}
          onClick={() => {
            popupState.close();
            onConfirmNotiAll();
          }}
        >
          모두 읽음
        </Button>
        <Button size="small" variant="contained" onClick={popupState.close}>
          닫기
        </Button>
      </Stack>
    </>
  );

  return (
    <PopupState variant="popover" popupId="NotiPopover">
      {(popupState) => (
        <Box>
          <IconButton color="inherit" {...bindTrigger(popupState)}>
            <Badge badgeContent={notiCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {isMobile ? (
            <Drawer
              anchor="bottom"
              open={popupState.isOpen}
              onClose={popupState.close}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  overflow: 'hidden',
                },
              }}
            >
              {renderContent(popupState)}
            </Drawer>
          ) : (
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {renderContent(popupState)}
            </Popover>
          )}
        </Box>
      )}
    </PopupState>
  );
};

export default NotiBox;
